import { useRef } from 'react';
import styled, { css } from 'styled-components';
import { APIComponentAttributes, APISurveyAttributes } from '@ardoq/api-types';
import { colors, s16, s4, s64, shadowM, spacing } from '@ardoq/design-tokens';
import {
  ButtonGroup,
  DangerButton,
  PrimaryButton,
  SecondaryButton,
} from '@ardoq/button';
import { Icon, IconName } from '@ardoq/icons';
import { useEffectOnce } from '@ardoq/hooks';
import { MarkdownViewer } from '@ardoq/markdown';
import { StatusType, Tag } from '@ardoq/status-ui';
import { Header3 } from '@ardoq/typography';
import { Box } from '@ardoq/layout';
import { t, type I18nLocale } from '@ardoq/i18n';
import { tourAnchorIds } from '@ardoq/product-tours';
import { SurveyDescription } from './atoms';
import { SurveyQuestions } from './questions/SurveyQuestions';
import type { SurveyQuestionProps } from './questions/SurveyQuestion';

const SURVEY_WRAPPER_MAX_WIDTH = 1000;

const SurveyWrapper = styled.div`
  max-width: ${SURVEY_WRAPPER_MAX_WIDTH}px;
  margin: 0px auto;
  padding: min(24px, calc((100vw - ${SURVEY_WRAPPER_MAX_WIDTH}px) / 2)) 0;
  margin-bottom: 120px;
`;

const SurveyInner = styled.div<{ $isInIframe: boolean }>`
  border: 1px solid #d6d9df;
  box-shadow: ${shadowM};
  background-color: #ffffff;
  margin-bottom: 5rem;
  ${props =>
    props.$isInIframe &&
    css`
      border: none;
      box-shadow: none;
      margin-bottom: ${s64};
      padding-bottom: ${s64};
    `}
`;

const Content = styled.div<{
  $isLocked: boolean;
  $isInIframe: boolean;
}>`
  ${({ $isLocked }) =>
    $isLocked && 'background-color: rgba(236, 236, 236, 0.2);'}
  /* 585px is the minimum height for a select with the dropdown open */
  min-height: 585px;
  align-items: center;
  border-top: 1px solid #d6d9df;
  border-radius: 0 0 2px 2px;
  & > button {
    margin: 1rem 0 0 2.25rem;
  }
  & > div {
    padding: ${spacing.s24};
  }
  ${({ $isInIframe }) =>
    $isInIframe &&
    css`
      border-top: none;
      & > div {
        padding: ${spacing.s24} ${spacing.s32};
      }
    `}
`;

const Actions = styled.div<{ $isInIframe: boolean }>`
  text-align: center;
  padding: ${spacing.s24} 0;
  z-index: 10;
  ${props =>
    !props.$isInIframe
      ? css`
          @media only screen and (min-width: 1024px) {
            position: fixed;
            background-color: #fff;
            right: 0px;
            bottom: 0px;
            left: 0px;
            padding: ${spacing.s16} 0;
            box-shadow: ${shadowM};
          }
        `
      : css`
          position: fixed;
          background-color: ${colors.grey50};
          right: 0px;
          bottom: 0px;
          left: 0px;
          padding: ${spacing.s16} 0;
          border-top: 1px solid ${colors.grey200};
        `}
`;

const Error = styled.div`
  color: #cc3445;
  margin: 16px 0;
`;

const ButtonsFooter = styled.div`
  display: flex;
  justify-content: space-evenly;
`;

const StyledSurveyDescription = styled(SurveyDescription)`
  background-color: ${colors.grey50};
  border-radius: ${s4};
  border: 1px solid ${colors.grey300};
  padding: ${s16};
`;

type SurveyQuestionsPassThroughProps = Pick<
  SurveyQuestionProps,
  | 'QuestionContentWrapper'
  | 'fields'
  | 'answers'
  | 'currentLocale'
  | 'setAnswer'
  | 'getDuplicate'
  | 'getExistingComponentByName'
  | 'setDependentQuestionAnswer'
  | 'addNestedAnswer'
  | 'setNestedAnswer'
  | 'removeNestedAnswer'
  | 'addNestedDependentAnswer'
  | 'removeNestedDependentAnswer'
  | 'updateReferenceComponent'
  | 'generateComponentDescription'
  | 'getAISuggestions'
  | 'fetchComponentOptionsWithQuery'
  | 'userOptions'
  | 'fetchTags'
  | 'trackDescriptionGenerated'
  | 'trackDescriptionGeneratedFailed'
  | 'trackCreatedReferenceFromQuestion'
>;

export type SurveyFormViewProps = SurveyQuestionsPassThroughProps & {
  survey: APISurveyAttributes;
  component: APIComponentAttributes | null;
  i18nLocale: I18nLocale;

  title: string;
  submitButtonText: string;
  deleteButtonText: string;
  errorMessage: string | undefined;

  broadcastInstanceId?: string;
  componentId: string | undefined;

  isEmbedded: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  isNewComponent: boolean;
  isPendingApproval: boolean;
  hasAttemptedSubmission: boolean;
  hasValidationErrors: boolean;
  hasSurveyResponseApprovalsFeature: boolean;
  hasAutocompleteFeature: boolean;
  hasReferenceSuggestionsFeature: boolean;
  hasEditableReferencesInQuestionsFeature: boolean;

  onCancel: VoidFunction;
  onFormLoaded: (scrollHeight: number | undefined) => void;
  saveAnswers: VoidFunction;
  deleteSurveyComponent: VoidFunction;
};

const SurveyFormView = ({
  survey,
  component,
  i18nLocale,
  title,
  submitButtonText,
  deleteButtonText,
  errorMessage,
  broadcastInstanceId,
  componentId,
  isEmbedded,
  isSaving,
  isDeleting,
  isNewComponent,
  isPendingApproval,
  hasAttemptedSubmission,
  hasValidationErrors,
  hasSurveyResponseApprovalsFeature,
  hasAutocompleteFeature,
  hasReferenceSuggestionsFeature,
  hasEditableReferencesInQuestionsFeature,
  onCancel,
  onFormLoaded,
  saveAnswers,
  deleteSurveyComponent,
  ...questionsProps
}: SurveyFormViewProps) => {
  const lockIcon = (
    <Icon
      iconName={IconName.LOCK}
      data-tooltip-text={t(
        i18nLocale,
        'surveysUi:surveyForm.lockedTooltip',
        'This component has been locked and cannot be deleted or edited.'
      )}
    />
  );
  const { lock = false } = component ?? {};
  const isLocked = typeof lock === 'string';

  const surveyFormRef = useRef<HTMLDivElement | null>(null);

  useEffectOnce(() => {
    if (!isEmbedded) return;
    onFormLoaded(surveyFormRef?.current?.scrollHeight);
  });

  return (
    <SurveyWrapper ref={surveyFormRef}>
      <SurveyInner $isInIframe={isEmbedded}>
        {!isEmbedded ? (
          <Box padding="large">
            <Header3>
              {title} {isLocked && lockIcon}
            </Header3>
          </Box>
        ) : survey.description ? (
          <StyledSurveyDescription>
            <MarkdownViewer
              content={survey.description}
              sanitationLevel="permissive"
            />
          </StyledSurveyDescription>
        ) : null}
        <Content $isInIframe={isEmbedded} $isLocked={isLocked}>
          {hasSurveyResponseApprovalsFeature && isPendingApproval && (
            <div>
              <Tag
                label={t(
                  i18nLocale,
                  'surveysUi:surveyForm.pendingApprovalLabel',
                  'Pending approval'
                )}
                statusType={StatusType.INFO}
                iconName={IconName.INFO}
                data-tooltip-text={t(
                  i18nLocale,
                  'surveysUi:surveyForm.pendingApprovalTooltip',
                  'This survey contains some answers which are pending approval'
                )}
              />
            </div>
          )}
          <SurveyQuestions
            {...questionsProps}
            isSaving={isSaving}
            isLocked={isLocked}
            broadcastInstanceId={broadcastInstanceId}
            survey={survey}
            componentId={componentId}
            i18nLocale={i18nLocale}
            hasAttemptedSubmission={hasAttemptedSubmission}
            hasAutocompleteFeature={hasAutocompleteFeature}
            hasReferenceSuggestionsFeature={hasReferenceSuggestionsFeature}
            hasEditableReferencesInQuestionsFeature={
              hasEditableReferencesInQuestionsFeature
            }
            isEmbedded={isEmbedded}
            hideReadOnlyLabel={false}
            forcedReadonly={false}
            highlightUnansweredPriorityQuestions={hasAttemptedSubmission}
          />
        </Content>
        {survey.published && (
          <Actions $isInIframe={isEmbedded}>
            {errorMessage && <Error>{errorMessage}</Error>}
            <ButtonsFooter>
              {survey.allowComponentDeletion && !isNewComponent && (
                <ButtonGroup>
                  <DangerButton
                    type="button"
                    isDisabled={isLocked || isDeleting}
                    onClick={deleteSurveyComponent}
                    dataTestId="delete-button"
                  >
                    {deleteButtonText}
                  </DangerButton>
                </ButtonGroup>
              )}
              <ButtonGroup>
                <SecondaryButton
                  isDisabled={isSaving}
                  onClick={onCancel}
                  dataTestId="cancel-button"
                >
                  {t(i18nLocale, 'surveysUi:surveyForm.cancel', 'Cancel')}
                </SecondaryButton>
                <PrimaryButton
                  data-tour-id={tourAnchorIds.surveyFormSubmit}
                  type="submit"
                  isDisabled={
                    isSaving || (hasValidationErrors && hasAttemptedSubmission)
                  }
                  onClick={saveAnswers}
                  dataTestId="submit-button"
                >
                  {submitButtonText}
                </PrimaryButton>
              </ButtonGroup>
            </ButtonsFooter>
          </Actions>
        )}
      </SurveyInner>
    </SurveyWrapper>
  );
};

export { SurveyFormView };
